import React, { useState, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import {
    useSystemStatus,
    useSystemConfiguration,
} from '../api/system-configuration/use-system-configuration';
import { useImpersonationStatus } from '../api/impersonation/use-impersonation';
import LanguageSelectorDropdown from '../theme/LanguageSelectorDropdown';
import ColorModeIconDropdown from '../theme/ColorModeIconDropdown';
import { GLASS_ICON_BUTTON } from '@/components/shared/glass';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { useCrmShellAttribute } from './crm/use-crm-shell';
import BrandMark from './crm/BrandMark';

const AuthLayout: React.FC = () => {
    const { isAuthenticated, isLoading: authLoading, isAdmin, isCRM } = useAuth();
    const {
        tenant,
        isLoading: tenantLoading,
        error: tenantError,
        errorType: tenantErrorType,
        refreshTenant,
    } = useTenant();
    const { data: systemStatus, isLoading: systemLoading, error: systemError } = useSystemStatus();
    const { data: systemConfig, isLoading: systemConfigLoading } = useSystemConfiguration();
    const { isLoading: impersonationLoading, error: impersonationError } = useImpersonationStatus();
    const location = useLocation();
    const { t } = useTranslation('common');

    // CRM sign-in pages wear the CRM shell (backdrop glow, tokens, density —
    // see docs/CRM_DESIGN_SYSTEM.md). The trader portal and the tenant admin
    // share this layout and keep their own look.
    const crmShell = isCRM();
    useCrmShellAttribute(crmShell);
    const screen = crmShell ? 'crm-backdrop' : 'bg-background';

    // Track if we've completed the initial load
    const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);

    // Check if any of the required data is loading
    const isAnyLoading =
        authLoading ||
        tenantLoading ||
        systemLoading ||
        systemConfigLoading ||
        impersonationLoading;

    // Mark as initially loaded once all data has finished loading for the first time
    useEffect(() => {
        if (!isAnyLoading && !hasInitiallyLoaded) {
            setHasInitiallyLoaded(true);
        }
    }, [isAnyLoading, hasInitiallyLoaded]);

    // Only show loader on initial load, not on refetches
    const isLoading = !hasInitiallyLoaded && isAnyLoading;

    // Simplified error handling logic
    const hasCriticalError =
        tenantError ||
        systemStatus?.maintenance_mode === true ||
        (systemError && systemError.message?.includes('500')) ||
        (impersonationError && impersonationError.message?.includes('500'));

    if (isLoading) {
        return (
            <div className={cn('flex min-h-screen flex-col items-center justify-center', screen)}>
                <div className="flex flex-col items-center space-y-4">
                    {/* Main loader */}
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>

                    {/* Simple loading text */}
                    <p className="text-foreground font-medium">Loading...</p>
                </div>
            </div>
        );
    }

    // Show error state if there are critical errors
    if (hasCriticalError) {
        const isMaintenanceMode = systemStatus?.maintenance_mode === true;
        const isServerDown = tenantErrorType === 'network';
        const isPlatformNotFound = tenantError && !isServerDown;
        const isServerError =
            tenantErrorType === 'server' ||
            (systemError && systemError.message?.includes('500')) ||
            (impersonationError && impersonationError.message?.includes('500'));

        if (isMaintenanceMode) {
            return (
                <div
                    className={cn(
                        'flex min-h-screen flex-col items-center justify-center p-4',
                        screen
                    )}
                >
                    <div className="w-full max-w-md text-center">
                        <div className="mb-8">
                            <div className="w-20 h-20 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                <svg
                                    className="w-10 h-10 text-amber-500"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={1.5}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z"
                                    />
                                </svg>
                            </div>
                            <h1 className="text-2xl font-bold text-foreground mb-3">
                                Scheduled Maintenance
                            </h1>
                            <p className="text-muted-foreground leading-relaxed">
                                {systemStatus?.maintenance_message ||
                                    "We're performing scheduled maintenance to improve your experience. We'll be back shortly."}
                            </p>
                        </div>
                        <p className="text-sm text-muted-foreground">
                            Please check back in a few minutes.
                        </p>
                    </div>
                </div>
            );
        }

        if (isServerDown) {
            return (
                <div
                    className={cn(
                        'flex min-h-screen flex-col items-center justify-center p-4',
                        screen
                    )}
                >
                    <div className="w-full max-w-md text-center">
                        <div className="mb-8">
                            <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
                                <svg
                                    className="w-10 h-10 text-muted-foreground"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={1.5}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z"
                                    />
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M12 9v3.75m0 3.75h.008v.008H12v-.008z"
                                    />
                                </svg>
                            </div>
                            <h1 className="text-2xl font-bold text-foreground mb-3">
                                Service Temporarily Unavailable
                            </h1>
                            <p className="text-muted-foreground leading-relaxed mb-6">
                                We're having trouble connecting to our servers right now. This is
                                usually temporary — please try again in a moment.
                            </p>
                            <button
                                onClick={() => refreshTenant()}
                                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                            >
                                <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                                    />
                                </svg>
                                Try Again
                            </button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            If this problem continues, please contact support.
                        </p>
                    </div>
                </div>
            );
        }

        if (isPlatformNotFound) {
            return (
                <div
                    className={cn(
                        'flex min-h-screen flex-col items-center justify-center p-4',
                        screen
                    )}
                >
                    <div className="w-full max-w-md text-center">
                        <div className="mb-8">
                            <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
                                <svg
                                    className="w-10 h-10 text-muted-foreground"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={1.5}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                                    />
                                </svg>
                            </div>
                            <h1 className="text-2xl font-bold text-foreground mb-3">
                                Platform Not Found
                            </h1>
                            <p className="text-muted-foreground leading-relaxed">
                                The address you're trying to reach doesn't match any active
                                platform. Please check your link or contact your administrator for
                                the correct URL.
                            </p>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            If you believe this is a mistake, please contact support.
                        </p>
                    </div>
                </div>
            );
        }

        // Server error / unknown fallback
        return (
            <div
                className={cn('flex min-h-screen flex-col items-center justify-center p-4', screen)}
            >
                <div className="w-full max-w-md text-center">
                    <div className="mb-8">
                        <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-6">
                            <svg
                                className="w-10 h-10 text-destructive"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={1.5}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                                />
                            </svg>
                        </div>
                        <h1 className="text-2xl font-bold text-foreground mb-3">
                            Something Went Wrong
                        </h1>
                        <p className="text-muted-foreground leading-relaxed mb-6">
                            An unexpected error occurred. Please try refreshing the page.
                        </p>
                        <button
                            onClick={() => window.location.reload()}
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                        >
                            <svg
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                                />
                            </svg>
                            Refresh Page
                        </button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                        If this problem continues, please contact support.
                    </p>
                </div>
            </div>
        );
    }

    if (isAuthenticated) {
        const from = location.state?.from?.pathname || '/';
        return <Navigate to={from} replace />;
    }

    // Get tenant name and system name
    const tenantName =
        tenant?.tenantName ||
        tenant?.systemName ||
        systemStatus?.system_name ||
        systemConfig?.system_name ||
        'Quantix';
    const systemName =
        tenant?.systemName ||
        systemStatus?.system_name ||
        systemConfig?.system_name ||
        'Quantix Trading';

    // Get logo URL from system configuration
    const logoUrl = systemConfig?.system_logo_url || systemConfig?.logo_url;

    // Get first letter of system name for fallback
    const firstLetter = systemName.charAt(0).toUpperCase();

    return (
        <div
            className={cn(
                'relative flex min-h-screen items-center justify-center p-4',
                crmShell ? 'crm-backdrop' : 'bg-background'
            )}
        >
            <div className="absolute end-4 top-4 flex items-center gap-1.5">
                {crmShell ? (
                    <>
                        <LanguageSelectorDropdown compact className={GLASS_ICON_BUTTON} />
                        <ColorModeIconDropdown className={GLASS_ICON_BUTTON} />
                    </>
                ) : (
                    <LanguageSelectorDropdown />
                )}
            </div>
            <div className="w-full max-w-md">
                {/* Logo and Platform Name */}
                <div className="mb-8 flex items-center justify-center">
                    {crmShell ? (
                        <BrandMark logoUrl={logoUrl} name={systemName} />
                    ) : logoUrl ? (
                        <img src={logoUrl} alt={systemName} className="h-16 w-48 object-contain" />
                    ) : (
                        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-primary/80 shadow-lg">
                            <span className="text-2xl font-bold text-primary-foreground">
                                {firstLetter}
                            </span>
                        </div>
                    )}
                </div>

                {/* Auth Form. CRM: a frosted panel over the brand backdrop,
                    matching the shell chrome. */}
                <div
                    className={cn(
                        crmShell
                            ? 'rounded-[calc(var(--radius)+6px)] border border-glass-nav-border bg-background/80 p-6 shadow-[var(--shadow-lg)] backdrop-blur-xl backdrop-saturate-150 sm:p-8'
                            : 'rounded-2xl border border-border bg-card p-8 shadow-lg'
                    )}
                >
                    <Outlet />
                </div>

                {/* Footer */}
                <div className="mt-6 text-center">
                    <p className="text-xs text-muted-foreground">
                        {t('allRightsReserved', {
                            year: new Date().getFullYear(),
                            name: tenantName,
                        })}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default AuthLayout;
